import { createContext, useContext, useState } from 'react';
import { type StateCreator, type StoreApi, create, useStore as useZustandStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  type ReduxDevtoolsActionContext,
  createReduxDevtoolsMiddleware,
  isDebugStoresDevtoolsEnabled,
  registerReduxDevtoolsStore,
  sendDevtoolsState,
  toReduxDevtoolsStoreName,
  wrapActionsWithReduxDevtools,
} from '../logging/reduxDevtools';
import type { ReactElement } from 'react';

/**
 * Set store state function type for Immer-style draft updates
 */
export type SetStoreState<S> = (updater: (draft: S) => void) => void;

/**
 * Get store state function type
 */
export type GetStoreState<S> = () => S;

/**
 * Bound reset helper for a store instance.
 */
export type ResettableStoreState<S> = (overrides?: Partial<S>) => void;

/**
 * Standard reset action shape for stores that expose reset through their public actions API.
 */
export type ResettableStoreActions<S> = {
  reset: ResettableStoreState<S>;
};

/**
 * Store helper methods for stores that can be reset back to their initial baseline.
 */
export type ResettableStore<S> = StoreApi<S> & {
  /**
   * Replaces state with the merged initial state plus any supplied overrides.
   */
  resetState: (overrides?: Partial<S>) => void;
};

/**
 * Actions factory function that receives store helpers and returns action functions
 */
export type ActionsFactory<S, A> = (
  set: SetStoreState<S>,
  get: GetStoreState<S>,
  resetState: ResettableStoreState<S>,
) => A;

/**
 * Zustand middleware type
 * A middleware is a function that wraps the state creator
 */
export type Middleware<S> = (config: StateCreator<S, [], []>) => StateCreator<S, [], []>;

export type CreateStoreInstanceOptions<S extends object> = {
  middlewares?: Middleware<S>[];
  storeName?: string;
};

/**
 * Store owner API exposed by `useStore`.
 */
export type StoreInstance<S, A> = ResettableStore<S> & {
  /**
   * Actions object with all mutation functions
   * Stable reference, never causes re-renders when accessed
   */
  actions: A;
};

/**
 * Store instance whose public actions API includes reset semantics.
 */
export type ResettableStoreInstance<S, A extends ResettableStoreActions<S>> = StoreInstance<S, A> & ResettableStore<S>;

/**
 * Reactive selector hook bound to a store context.
 */
export type UseSelector<S> = <U>(selector: (state: S) => U) => U;

/**
 * Props for the generated Provider component.
 */
export type ProviderProps<S, A> = {
  children: React.ReactNode;
  initialState?: Partial<S>;
  /**
   * @deprecated Legacy prop for backwards compatibility only - DO NOT USE in new code or tests!
   *
   * This prop allows passing a pre-created store instance to the Provider, which is ONLY used
   * during migration from Flux/Baobab stores to maintain singleton access patterns (e.g., AxiosStore).
   *
   * **New code should NOT use this prop.** Let the Provider create its own store instance.
   * This ensures proper test isolation and follows React best practices.
   */
  store?: StoreInstance<S, A>;
};

/**
 * Creates a named store factory and the matching React bindings for it.
 *
 * Returns `[Provider, useStore, useSelector]`.
 * See `state/README.md` for examples, selector guidance, and action organization patterns.
 */
// Overload: with actions and middlewares
export function createStore<S extends object, A extends object>(
  storeName: string,
  initialState: S,
  actionsFactory: ActionsFactory<S, A>,
  middlewares?: Middleware<S>[],
): [(props: ProviderProps<S, A>) => ReactElement | null, () => StoreInstance<S, A>, UseSelector<S>];

// Overload: without actions
export function createStore<S extends object>(
  storeName: string,
  initialState: S,
): [(props: ProviderProps<S, object>) => ReactElement | null, () => StoreInstance<S, object>, UseSelector<S>];

// Implementation
export function createStore<S extends object, A extends object>(
  storeName: string,
  initialState: S,
  actionsFactory?: ActionsFactory<S, A>,
  middlewares?: Middleware<S>[],
): [(props: ProviderProps<S, A>) => ReactElement | null, () => StoreInstance<S, A>, UseSelector<S>] {
  // Clone initial state immediately so later external mutations do not affect future Providers.
  const clonedInitialState = structuredClone(initialState);

  // Create the context for this store
  const StoreContext = createContext<StoreInstance<S, A> | null>(null);

  // Provider component that creates a store instance
  const Provider = ({ children, initialState: providerInitialState, store: externalStore }: ProviderProps<S, A>) => {
    // Use useState with function initializer for one-time store creation
    const [store] = useState<StoreInstance<S, A>>(() => {
      // Use externally provided store if available (legacy pattern)
      if (externalStore) {
        return externalStore;
      }

      // Otherwise create a new store instance
      const mergedInitialState = {
        ...clonedInitialState,
        ...(providerInitialState ? structuredClone(providerInitialState) : {}),
      };

      return createStoreInstance(mergedInitialState, actionsFactory, { middlewares, storeName });
    });

    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
  };

  // Set displayName for better debugging in React DevTools
  Provider.displayName = `${storeName}StoreProvider`;

  // Hook to get store instance (for actions, getState, subscribe, etc)
  const useStore = (): StoreInstance<S, A> => {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error(
        `A hook is attempting to access the ${storeName} store but its companion <${storeName}StoreProvider> can't be found. Did you forget a <${storeName}StoreProvider> around your component?`,
      );
    }

    return store;
  };

  // Selectors should return stable values or use memoized selector helpers for derived objects/arrays.
  const useSelector = (<U,>(selector: (state: S) => U): U => {
    const store = useStore();
    return useZustandStore(store, selector);
  }) as UseSelector<S>;

  return [Provider, useStore, useSelector];
}

type StoreCreator<S extends object> = StateCreator<S, [], []>;
type DraftUpdater<S> = Parameters<SetStoreState<S>>[0];
type DraftSetState<S extends object> = (updater: DraftUpdater<S>) => void;

/**
 * Creates a plain store instance without React context or hooks.
 *
 * Use this for tests, non-React consumers, or legacy singleton wiring.
 */
export function createStoreInstance<S extends object, A extends object>(
  initialState: S,
  actionsFactory?: ActionsFactory<S, A>,
  options?: CreateStoreInstanceOptions<S>,
): StoreInstance<S, A> {
  const { middlewares = [], storeName } = options ?? {};

  // Deep-clone the initial state immediately to prevent external mutations from affecting future store instances.
  const clonedInitialState = structuredClone(initialState);

  const baseStateCreator: StoreCreator<S> = () => clonedInitialState;

  // Apply middlewares from inside out
  let storeCreator: StoreCreator<S> = immer(baseStateCreator as never) as unknown as StoreCreator<S>;

  // First apply immer (innermost)
  for (let i = middlewares.length - 1; i >= 0; i--) {
    storeCreator = middlewares[i](storeCreator);
  }

  const devtoolsActionContext: ReduxDevtoolsActionContext = {};
  if (storeName && isDebugStoresDevtoolsEnabled()) {
    storeCreator = createReduxDevtoolsMiddleware<S>(storeName, devtoolsActionContext)(storeCreator);
  }

  const storeInstance = create<S>()(storeCreator);
  if (storeName && isDebugStoresDevtoolsEnabled()) {
    const devtoolsStoreName = toReduxDevtoolsStoreName(storeName);
    registerReduxDevtoolsStore(devtoolsStoreName, () => ({ state: storeInstance.getState() }));
    sendDevtoolsState({ type: `${devtoolsStoreName}/@@INIT` });
  }
  const resetState = (overrides?: Partial<S>) => {
    storeInstance.setState(
      {
        // Deep-clone the initial state so external mutations to current store's state can't corrupt future resets.
        ...structuredClone(clonedInitialState),
        ...(overrides ? structuredClone(overrides) : {}),
      },
      true,
    );
  };
  const store: ResettableStore<S> = Object.assign(storeInstance, {
    resetState,
  });
  const setDraftState = storeInstance.setState as unknown as DraftSetState<S>;

  // Create actions if factory provided
  const actions = actionsFactory
    ? actionsFactory(
        // Wrap setState to accept immer-style draft updaters
        // After applying immer middleware, setState's type is transformed to accept (draft: Draft<S>) => void
        // Cast to any because TypeScript can't track the type transformation through middleware
        (updater) => setDraftState(updater),
        () => storeInstance.getState(),
        resetState,
      )
    : ({} as A);

  let allActions = actions;
  if (storeName && isDebugStoresDevtoolsEnabled()) {
    allActions = wrapActionsWithReduxDevtools(actions, storeName, devtoolsActionContext);
  }

  // Augment store with actions
  return Object.assign(store, { actions: allActions });
}
